import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import * as XLSX from 'xlsx';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { SessionStorageService } from '../../services/session-storage.service';
import { ShipmentBulkImportBatch, ShipmentBulkImportPreview, ShipmentBulkImportResource, ShipmentBulkImportRow, ShipmentBulkImportStatus } from './shipment-bulk-upload.model';
import { ShipmentBulkUploadService } from './shipment-bulk-upload.service';

@Component({
  selector: 'app-shipment-bulk-upload',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ToastModule,
    BreadcrumbModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TagModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent
  ],
  templateUrl: './shipment-bulk-upload.component.html',
  styleUrls: ['./shipment-bulk-upload.component.css']
})
export class ShipmentBulkUploadComponent implements OnInit {
  breadcrumbItems: MenuItem[] = [{ label: 'Shipments', routerLink: '/shipment/company' }, { label: 'Bulk Upload' }];
  homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };

  file?: File;
  isLoading = false;
  processing = false;
  current?: ShipmentBulkImportPreview;
  batches: ShipmentBulkImportBatch[] = [];
  selectedStatus: ShipmentBulkImportStatus | 'ALL' = 'ALL';
  selectedResource: ShipmentBulkImportResource | 'ALL' = 'ALL';
  editDialogVisible = false;
  editingRow?: ShipmentBulkImportRow;
  editingValues: Record<string, string> = {};

  statusOptions = [
    { label: 'All statuses', value: 'ALL' },
    { label: 'Valid', value: 'VALID' },
    { label: 'Invalid', value: 'INVALID' },
    { label: 'Success', value: 'SUCCESS' },
    { label: 'Failed', value: 'FAILED' }
  ];
  resourceOptions = [
    { label: 'All resources', value: 'ALL' },
    { label: 'Shipments', value: 'SHIPMENT' },
    { label: 'Deliveries', value: 'DELIVERY' }
  ];

  shipmentColumns = [
    'externalShipmentRef', 'companyId', 'companyName', 'customerId', 'customerName', 'customerEmailAddress',
    'customerPhoneNumber', 'sourceLocationId', 'sourceLocationName', 'destinationLocationId',
    'destinationLocationName', 'pickupWindowStart', 'deliveryWindowEnd', 'requestedTruckCount',
    'vehicleTypeId', 'vehicleTypeName', 'vehicleConfigId', 'vehicleConfigName', 'vehicleCapacityId',
    'vehicleCapacityName', 'cargoTypeId', 'cargoTypeName', 'cargoDescription', 'quantity', 'uomId',
    'uomName', 'totalWeight', 'weightUnitId', 'weightUnitName', 'routeType', 'serviceCategory',
	'buyingPrice', 'sellingPrice', 'currencyCode', 'pricingRateSource', 'pricingOverrideReason', 'paymentTermId',
    'senderCompanyId', 'senderContactId', 'receiverCompanyId', 'receiverContactId'
  ];
  deliveryColumns = [
    'externalShipmentRef', 'externalDeliveryRef', 'driverId', 'driverPhoneNumber', 'driverEmailAddress',
    'vehicleId', 'vehicleRegistrationNumber', 'vehiclePlateNumber', 'scheduledShippingDate', 'jobWorth',
	'fareQuote', 'waitingCharge', 'insuranceAmount', 'paymentTerms', 'payloadQuantity'
  ];

  constructor(
    private service: ShipmentBulkUploadService,
    private session: SessionStorageService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.loadBatches();
  }

  get filteredRows(): ShipmentBulkImportRow[] {
    return (this.current?.rows || []).filter(row =>
      (this.selectedStatus === 'ALL' || row.status === this.selectedStatus) &&
      (this.selectedResource === 'ALL' || row.resource === this.selectedResource)
    );
  }

  metrics(): PageMetric[] {
    const batch = this.current?.batch;
    return [
      { label: 'Total Rows', value: batch?.totalRows || 0, hint: 'Parsed shipment and delivery rows', tone: 'default' },
      { label: 'Valid', value: batch?.validRows || 0, hint: 'Ready to process', tone: 'success' },
      { label: 'Invalid', value: batch?.invalidRows || 0, hint: 'Needs correction', tone: 'warn' },
      { label: 'Processed', value: batch?.successRows || 0, hint: `${batch?.failedRows || 0} failed`, tone: 'info' }
    ];
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.file = input.files?.[0] || undefined;
  }

  upload(): void {
    const company = this.session.getCompany();
    if (!this.file || !company?.id) {
      this.messageService.add({ severity: 'warn', summary: 'Missing data', detail: 'Select an XLSX file after company context is loaded.' });
      return;
    }
    this.isLoading = true;
    this.service.upload(this.file, company.id).subscribe({
      next: response => {
        this.current = this.preparePreview(response.data);
        this.loadBatches();
        this.messageService.add({ severity: 'success', summary: 'Uploaded', detail: 'Rows are ready for review.' });
        this.isLoading = false;
      },
      error: error => {
        this.messageService.add({ severity: 'error', summary: 'Upload failed', detail: error?.error?.message || error.message });
        this.isLoading = false;
      }
    });
  }

  loadBatches(): void {
    const companyId = this.session.getCompany()?.id;
    if (!companyId) return;
    this.service.batches(companyId).subscribe(response => this.batches = response.items || []);
  }

  openBatch(batch: ShipmentBulkImportBatch): void {
    this.isLoading = true;
    this.service.batch(batch.batchId).subscribe({
      next: response => {
        this.current = this.preparePreview(response.data);
        this.isLoading = false;
      },
      error: () => this.isLoading = false
    });
  }

  processBatch(): void {
    if (!this.current?.batch?.batchId) return;
    this.processing = true;
    this.service.processBatch(this.current.batch.batchId).subscribe({
      next: response => {
        this.current = this.preparePreview({ batch: response.data.batch, rows: response.data.rows });
        this.loadBatches();
        this.processing = false;
        this.messageService.add({ severity: 'success', summary: 'Processed', detail: 'Valid rows were processed.' });
      },
      error: error => {
        this.processing = false;
        this.messageService.add({ severity: 'error', summary: 'Process failed', detail: error?.error?.message || error.message });
      }
    });
  }

  retryRow(row: ShipmentBulkImportRow): void {
    row.status = 'PROCESSING';
    this.service.processRow(row.rowId).subscribe({
      next: response => {
        Object.assign(row, this.prepareRow(response.data));
        this.refreshCurrentBatch();
      },
      error: error => {
        this.messageService.add({ severity: 'error', summary: 'Retry failed', detail: error?.error?.message || error.message });
        this.refreshCurrentBatch();
      }
    });
  }

  openEdit(row: ShipmentBulkImportRow): void {
    this.editingRow = this.prepareRow({ ...row, values: { ...(row.values || {}) } });
    this.editingValues = { ...(this.editingRow.values || {}) };
    this.editDialogVisible = true;
  }

  saveRow(): void {
    if (!this.editingRow) return;
    this.editingRow.values = this.editingValues;
    this.service.updateRow(this.editingRow).subscribe({
      next: response => {
        const index = this.current?.rows.findIndex(row => row.rowId === response.data.rowId) ?? -1;
        if (this.current && index >= 0) this.current.rows[index] = this.prepareRow(response.data);
        this.editDialogVisible = false;
        this.refreshCurrentBatch();
        this.messageService.add({ severity: 'success', summary: 'Updated', detail: 'Row was revalidated.' });
      },
      error: error => this.messageService.add({ severity: 'error', summary: 'Update failed', detail: error?.error?.message || error.message })
    });
  }

  downloadTemplate(): void {
    this.service.template().subscribe(response => this.saveBlob(response.body!, 'shipment-bulk-import-template.xlsx'));
  }

  exportFailedRows(): void {
    const failed = (this.current?.rows || [])
      .filter(row => row.status === 'FAILED' || row.status === 'INVALID')
      .map(row => ({
        rowNumber: row.rowNumber,
        resource: row.resource,
        status: row.status,
        externalReference: row.externalReference,
        parentExternalReference: row.parentExternalReference,
        errorMessage: row.errorMessage,
        ...(row.values || {})
      }));
    const worksheet = XLSX.utils.json_to_sheet(failed);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Failed Rows');
    XLSX.writeFile(workbook, `shipment-import-failed-${Date.now()}.xlsx`);
  }

  rowColumns(row?: ShipmentBulkImportRow): string[] {
    if (!row) return [];
    return row.resource === 'SHIPMENT' ? this.shipmentColumns : this.deliveryColumns;
  }

  rowPrimary(row: ShipmentBulkImportRow): string {
    const values = row.values || {};
    return row.resource === 'SHIPMENT'
      ? values['externalShipmentRef'] || row.externalReference || '-'
      : values['externalDeliveryRef'] || row.externalReference || '-';
  }

  rowSecondary(row: ShipmentBulkImportRow): string {
    const values = row.values || {};
    return row.resource === 'SHIPMENT'
      ? [values['customerName'], values['sourceLocationName'], values['destinationLocationName']].filter(Boolean).join(' / ') || '-'
      : [values['externalShipmentRef'], values['driverPhoneNumber'], values['vehicleRegistrationNumber'] || values['vehiclePlateNumber']].filter(Boolean).join(' / ') || '-';
  }

  statusSeverity(status?: ShipmentBulkImportStatus): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' | undefined {
    if (status === 'SUCCESS' || status === 'COMPLETED') return 'success';
    if (status === 'VALID') return 'info';
    if (status === 'INVALID' || status === 'FAILED') return 'danger';
    if (status === 'PARTIAL_SUCCESS') return 'warn';
    return 'secondary';
  }

  private refreshCurrentBatch(): void {
    if (this.current?.batch?.batchId) {
      this.service.batch(this.current.batch.batchId).subscribe(response => this.current = this.preparePreview(response.data));
    }
  }

  private preparePreview(preview: ShipmentBulkImportPreview): ShipmentBulkImportPreview {
    return {
      batch: preview.batch,
      rows: (preview.rows || []).map(row => this.prepareRow(row))
    };
  }

  private prepareRow(row: ShipmentBulkImportRow): ShipmentBulkImportRow {
    row.values = row.values || this.parsePayload(row.rawPayload || row.normalizedPayload);
    return row;
  }

  private parsePayload(payload?: string): Record<string, string> {
    if (!payload) return {};
    try {
      return Object.entries(JSON.parse(payload)).reduce((acc, [key, value]) => {
        acc[key] = value == null ? '' : String(value);
        return acc;
      }, {} as Record<string, string>);
    } catch {
      return {};
    }
  }

  private saveBlob(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
  }
}
